import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "@components/Modal/Modal";
import { kycAPI } from "@api/main";
import { pageTrackingService } from "../../services/pageTracking";
import Loader from "@components/Loader/Loader";
import styles from "./KycVerificationModal.module.scss";
import {
  KYC_TERMS_INTRO,
  KYC_TERMS_SECTIONS,
  KYC_TERMS_TITLE,
} from "./kycTermsContent";

const AADHAAR_GROUP = 4;
const AADHAAR_GROUPS = 3;
const OTP_LEN = 6;

type Phase = "aadhaar" | "otp";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** When set, successful OTP verification runs this instead of reloading checkout (e.g. retry create_pay_session). */
  onVerificationSuccess?: () => void | Promise<void>;
};

function digitsOnly(s: string, max: number): string {
  return s.replace(/\D/g, "").slice(0, max);
}

function IconShield({ gradientId }: { gradientId: string }) {
  const gid = `kyc-shield-${gradientId}`;
  return (
    <svg className={styles.heroSvg} viewBox="0 0 48 48" aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.35)" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gid})`}
        d="M24 4L8 10v14c0 11 8 18 16 20 8-2 16-9 16-20V10L24 4zm0 8.2c3.2 1.8 5.4 5.2 5.4 9.1 0 4.5-2.8 8.3-6.8 9.8l-.6.2-.6-.2c-4-1.5-6.8-5.3-6.8-9.8 0-3.9 2.2-7.3 5.4-9.1z"
      />
    </svg>
  );
}

function IconSend() {
  return (
    <svg className={styles.btnIcon} viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg className={styles.btnIcon} viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

const KycVerificationModal: React.FC<Props> = ({ isOpen, onClose, onVerificationSuccess }) => {
  const shieldGradId = React.useId().replace(/:/g, "");
  const [phase, setPhase] = useState<Phase>("aadhaar");
  const [aadhaarParts, setAadhaarParts] = useState(["", "", ""]);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [otpParts, setOtpParts] = useState<string[]>(() => Array(OTP_LEN).fill(""));
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const aadhaarRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const otpRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const otpFull = otpParts.join("");

  const resetForm = useCallback(() => {
    setPhase("aadhaar");
    setAadhaarParts(["", "", ""]);
    setConsentAccepted(false);
    setOtpParts(Array(OTP_LEN).fill(""));
    setError(null);
    setInfo(null);
    setSubmitting(false);
    setShowTerms(false);
  }, []);

  const handleClose = () => {
    // Closed without completing verification — high-value purchase drop-off.
    pageTrackingService.trackKycSkipped();
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (isOpen && phase === "otp") {
      const t = requestAnimationFrame(() => otpRefs[0].current?.focus());
      return () => cancelAnimationFrame(t);
    }
  }, [isOpen, phase]);

  useEffect(() => {
    if (!showTerms) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setShowTerms(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showTerms]);

  const aadhaarFull = aadhaarParts.join("");

  const handleAadhaarChange = (index: number, raw: string) => {
    const v = digitsOnly(raw, AADHAAR_GROUP);
    const next = [...aadhaarParts];
    next[index] = v;
    setAadhaarParts(next);
    setError(null);
    setInfo(null);
    if (v.length === AADHAAR_GROUP && index < AADHAAR_GROUPS - 1) {
      aadhaarRefs[index + 1].current?.focus();
    }
  };

  const handleAadhaarKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && aadhaarParts[index] === "" && index > 0) {
      aadhaarRefs[index - 1].current?.focus();
    }
  };

  const handleOtpChange = (index: number, raw: string) => {
    const v = digitsOnly(raw, 1);
    const next = [...otpParts];
    next[index] = v;
    setOtpParts(next);
    setError(null);
    setInfo(null);
    if (v.length === 1 && index < OTP_LEN - 1) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && otpParts[index] === "" && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = digitsOnly(e.clipboardData.getData("text"), OTP_LEN);
    if (!pasted) return;
    const chars = pasted.split("").slice(0, OTP_LEN);
    const next = Array(OTP_LEN)
      .fill("")
      .map((_, i) => chars[i] ?? "");
    setOtpParts(next);
    setError(null);
    const focusIdx = Math.min(chars.length, OTP_LEN - 1);
    otpRefs[focusIdx].current?.focus();
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (aadhaarFull.length !== 12) {
      setError("Please enter all 12 digits of your Aadhaar number.");
      return;
    }
    if (!consentAccepted) {
      setError("Please accept the KYC Terms & Conditions and confirm verification to continue.");
      return;
    }

    setSubmitting(true);
    const result = await kycAPI.sendOtp(aadhaarFull);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    setInfo(
      "UIDAI is sending a one-time password by SMS to the mobile number registered for this Aadhaar. It may take a minute to arrive."
    );
    setPhase("otp");
    setOtpParts(Array(OTP_LEN).fill(""));
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (otpFull.length !== OTP_LEN) {
      setError("Please enter the 6-digit OTP.");
      return;
    }

    setSubmitting(true);
    const result = await kycAPI.verifyOtp(otpFull);
    setSubmitting(false);

    if (!result.ok) {
      pageTrackingService.trackKycSubmitted("failure", result.message);
      setError(result.message);
      return;
    }

    pageTrackingService.trackKycSubmitted("success");

    if (onVerificationSuccess) {
      resetForm();
      setSubmitting(false);
      await Promise.resolve(onVerificationSuccess());
      return;
    }

    const search = typeof window !== "undefined" ? window.location.search || "" : "";
    window.location.assign(`${window.location.origin}/checkout${search}`);
  };

  const goBackToAadhaar = () => {
    setPhase("aadhaar");
    setOtpParts(Array(OTP_LEN).fill(""));
    setError(null);
    setInfo(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className={styles.wrap}>
        <div className={styles.hero}>
          <div className={styles.heroGlow} aria-hidden />
          <div className={styles.heroGrid} aria-hidden />
          <div className={styles.heroTop}>
            <div className={styles.heroBrand}>
              <span className={styles.heroIconWrap}>
                <IconShield gradientId={shieldGradId} />
              </span>
              <div className={styles.heroTitles}>
                <span className={styles.heroEyebrow}>Encrypted · UIDAI OTP</span>
                <h2 className={styles.heroTitle}>Verify your identity</h2>
              </div>
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleClose}
              aria-label="Close"
            >
              <span className={styles.closeIcon} aria-hidden>
                ×
              </span>
            </button>
          </div>

          <ol className={styles.steps} aria-label="Verification steps">
            <li
              className={`${styles.step} ${phase === "aadhaar" ? styles.stepActive : styles.stepDone}`}
            >
              <span className={styles.stepNum} aria-hidden>
                {phase === "otp" ? "✓" : "1"}
              </span>
              <span className={styles.stepLabel}>Aadhaar</span>
            </li>
            <li className={styles.stepConnector} aria-hidden />
            <li className={`${styles.step} ${phase === "otp" ? styles.stepActive : ""}`}>
              <span className={styles.stepNum} aria-hidden>
                2
              </span>
              <span className={styles.stepLabel}>OTP</span>
            </li>
          </ol>
        </div>

        <div className={styles.body}>
          {error && <div className={styles.errorBanner}>{error}</div>}
          {info && !error && <div className={styles.infoBanner}>{info}</div>}

          <div className={styles.formCard}>
            {phase === "aadhaar" && (
              <form onSubmit={handleSendOtp} noValidate>
                <div className={styles.fieldBlock}>
                  <label className={styles.fieldLabelTitle} htmlFor="kyc-aadhaar-0">
                    Enter your Aadhaar number
                  </label>
                  <div
                    className={styles.aadhaarRow}
                    role="group"
                    aria-label="Aadhaar number 12 digits"
                  >
                    {aadhaarParts.map((part, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <span className={styles.aadhaarDash} aria-hidden />}
                        <input
                          id={index === 0 ? "kyc-aadhaar-0" : undefined}
                          ref={aadhaarRefs[index]}
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={AADHAAR_GROUP}
                          className={styles.aadhaarBox}
                          placeholder="••••"
                          aria-label={`Aadhaar digits ${index * 4 + 1}–${index * 4 + 4}`}
                          value={part}
                          onChange={(ev) => handleAadhaarChange(index, ev.target.value)}
                          onKeyDown={(ev) => handleAadhaarKeyDown(index, ev)}
                          aria-invalid={aadhaarFull.length > 0 && aadhaarFull.length < 12}
                          aria-describedby="kyc-aadhaar-helper"
                        />
                      </React.Fragment>
                    ))}
                  </div>
                  <p id="kyc-aadhaar-helper" className={styles.fieldHelper}>
                    Your Aadhaar details are secure and encrypted.
                  </p>
                </div>

                <div
                  className={`${styles.consentCard} ${consentAccepted ? styles.consentCardOn : ""}`}
                >
                  <div className={styles.consentInner}>
                    <input
                      id="kyc-consent-chk"
                      type="checkbox"
                      className={styles.consentCheckbox}
                      checked={consentAccepted}
                      onChange={(ev) => {
                        setConsentAccepted(ev.target.checked);
                        setError(null);
                      }}
                    />
                    <div className={styles.consentCopy}>
                      <p className={styles.consentText}>
                        <label htmlFor="kyc-consent-chk" className={styles.consentLabel}>
                          I agree to verify my identity using Aadhaar OTP for this transaction
                        </label>{" "}
                        and accept the{" "}
                        <button
                          type="button"
                          className={styles.termsLink}
                          onClick={(ev) => {
                            ev.preventDefault();
                            setShowTerms(true);
                          }}
                        >
                          KYC Terms & Conditions
                        </button>
                        .
                      </p>
                    </div>
                  </div>
                </div>

                <div className={styles.actions}>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={submitting || aadhaarFull.length !== 12 || !consentAccepted}
                  >
                    {submitting ? (
                      <span className={styles.submitBtnLoading}>
                        <Loader size={22} color="#FFFFFF" />
                        Sending…
                      </span>
                    ) : (
                      <>
                        <IconSend />
                        Send OTP
                      </>
                    )}
                  </button>
                  <button type="button" className={styles.secondaryBtn} onClick={handleClose}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {phase === "otp" && (
              <form onSubmit={handleVerifyOtp} noValidate>
                <div className={styles.fieldBlock}>
                  <label className={styles.fieldLabelTitle} htmlFor="kyc-otp-0">
                    Enter OTP
                  </label>
                  <div
                    className={styles.otpRow}
                    role="group"
                    aria-label="Enter OTP, 6 digits"
                    aria-describedby="kyc-otp-hint"
                  >
                    {otpParts.map((digit, index) => (
                      <input
                        key={index}
                        id={index === 0 ? "kyc-otp-0" : undefined}
                        ref={otpRefs[index]}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        maxLength={1}
                        className={styles.otpCell}
                        placeholder="·"
                        aria-label={`Digit ${index + 1} of 6`}
                        value={digit}
                        onChange={(ev) => handleOtpChange(index, ev.target.value)}
                        onKeyDown={(ev) => handleOtpKeyDown(index, ev)}
                        onPaste={handleOtpPaste}
                        aria-invalid={otpFull.length > 0 && otpFull.length < OTP_LEN}
                      />
                    ))}
                  </div>
                  <p id="kyc-otp-hint" className={styles.otpHint}>
                    Never share this OTP with anyone, including support.
                  </p>
                </div>

                <div className={styles.actions}>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={submitting || otpFull.length !== OTP_LEN}
                  >
                    {submitting ? (
                      <span className={styles.submitBtnLoading}>
                        <Loader size={22} color="#FFFFFF" />
                        Verifying…
                      </span>
                    ) : (
                      <>
                        <IconCheck />
                        Verify KYC
                      </>
                    )}
                  </button>
                  <button type="button" className={styles.secondaryBtn} onClick={goBackToAadhaar}>
                    Change Aadhaar / resend OTP
                  </button>
                  <button type="button" className={styles.secondaryBtn} onClick={handleClose}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className={styles.privacy}>
            Your Aadhaar is used only for mandatory KYC verification for this purchase.
          </p>
        </div>

        {showTerms && (
          <div
            className={styles.termsBackdrop}
            role="presentation"
            onClick={() => setShowTerms(false)}
          >
            <div
              className={styles.termsPanel}
              role="dialog"
              aria-modal="true"
              aria-labelledby="kyc-terms-title"
              onClick={(ev) => ev.stopPropagation()}
            >
              <div className={styles.termsPanelMobileHandle} aria-hidden />
              <div className={styles.termsPanelHeader}>
                <div className={styles.termsPanelTitleBlock}>
                  <h3 id="kyc-terms-title" className={styles.termsPanelTitle}>
                    {KYC_TERMS_TITLE}
                  </h3>
                  <p className={styles.termsPanelSubtitle}>Please read before you continue.</p>
                </div>
                <button
                  type="button"
                  className={styles.termsCloseBtn}
                  onClick={() => setShowTerms(false)}
                  aria-label="Close terms"
                >
                  ×
                </button>
              </div>
              <div className={styles.termsPanelBody}>
                <p className={styles.termsIntro}>{KYC_TERMS_INTRO}</p>
                {KYC_TERMS_SECTIONS.map((sec) => (
                  <section key={sec.heading} className={styles.termsSection}>
                    <h4 className={styles.termsSectionTitle}>{sec.heading}</h4>
                    {sec.lead && <p className={styles.termsLead}>{sec.lead}</p>}
                    <ul className={styles.termsList}>
                      {sec.items.map((item, i) => (
                        <li key={`${sec.heading}-${i}`} className={styles.termsListItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                    {sec.footer && <p className={styles.termsFooter}>{sec.footer}</p>}
                  </section>
                ))}
              </div>
              <div className={styles.termsPanelFooter}>
                <button
                  type="button"
                  className={styles.termsDoneBtn}
                  onClick={() => setShowTerms(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default KycVerificationModal;
